/* eslint-disable react/no-unstable-nested-components */
import React, { RefObject } from 'react';
import classNames from 'classnames';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ActionMenuButton from 'src/components/ui/ActionMenuButton/ActionMenuButton';
import { UseFiltersInterface } from '../../../services/filters';
import styles from '../../tables/ContactTable/ContactTable.module.scss';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import ColumnSelectModal from '../ColumnSelectModal/ColumnSelectModal';
import Icon from '../../ui/Icon/Icon';
import Select, { Option } from '../../ui/Select/Select';
import TablePagination from '../../ui/TablePagination/TablePagination';
import Checkbox from '../../ui/Checkbox/Checkbox';
import Badge, { BadgeType } from '../../ui/Badge/Badge';
import { getPaginationOptions, formatDate, formatTime } from '../../../utils/general';
import { useReturnsQuery } from '../../../services/returns';

export interface ReturnsTableProps {
  containerRef: RefObject<HTMLDivElement>;
  filterInstance: UseFiltersInterface;
}

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: (key: string) => string;
}

export function ReturnStateBadge({ status }: { status: any }) {
  const { t } = useTranslation();
  const badgeType: Record<string, BadgeType> = {
    angekündigt: BadgeType.Neutral2,
    wartet: BadgeType.Neutral4,
    'warten auf anderen vorgang': BadgeType.Warning,
    bereit: BadgeType.Neutral,
    erledigt: BadgeType.Successful,
    abgebrochen: BadgeType.Error,
  };

  return (
    <Badge
      className="max-w-min normal-case"
      label={t(`${status.label}`)}
      expanded
      colorOverride={status.color ? status.color : ''}
      type={status.color ? '' : badgeType[status.label?.toLowerCase()] ?? BadgeType.Neutral}
    />
  );
}

const ReturnsTable: React.FC<ReturnsTableProps> = ({ containerRef, filterInstance }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = {
    name: t('general.name'),
    date: t('general.date'),
    address: t('general.address'),
    country: t('general.country'),
    status: t('general.status'),
    note: t('general.note'),
  };
  const openOverlay = (id: number) => {
    navigate(`/returns/${id}`);
  };

  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.id, {
      id: 'checkbox',
      size: 60,
      enableHiding: false,
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: info => (
        <Checkbox
          checked={info.row.getIsSelected()}
          indeterminate={info.row.getIsSomeSelected()}
          onChange={info.row.getToggleSelectedHandler()}
        />
      ),
    }),
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full truncate"> {info.getValue()} </span>
          <span className="typo-text-12 text-faded truncate"> {info.row.original.partner.name} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.date, {
      id: 'date',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.date'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full truncate">{formatDate(info.row.original.date)}</span>
          <span className="typo-text-12 text-faded truncate">{formatTime(info.row.original.date)}</span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.partner.address.city, {
      id: 'partner_street',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.address'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14 truncate">{info.row.original.partner.address.street}</span>
          <div className="flex gap-2 w-full">
            <span className="typo-text-12 text-faded">{info.row.original.partner.address.zip_code}</span>
            <span className="typo-text-12 text-faded">{info.getValue()}</span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.partner.address.country_code, {
      id: 'partner_country',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.country'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14 text-full truncate"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.status, {
      id: 'state',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.status'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex">
          <span className="typo-text-14 text-full capitalize">
            <ReturnStateBadge status={info.getValue()} />
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      size: 80,
      enableHiding: false,
      header: () => <Icon className="text-full opacity-60 flex justify-center w-full" name="edit" />,
      cell: info => (
        <ActionMenuButton
          menuItems={[
            {
              onClick: () => openOverlay(info.row.original.id as number),
              label: t('general.view_details'),
            },
            {
              accessoryItem: 'local-shipping',
              label: t('returns.new_outbound_delivery'),
            },
          ]}
        />
      ),
    }),
  ];

  const [showColumnSelect, setShowColumnSelect] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [rowSelection, setRowSelection] = React.useState({});

  const options = getPaginationOptions();

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: parseInt(options[1].value, 10),
  });

  const sort = sorting[0];
  const { data: returns, isLoading: loading } = useReturnsQuery({
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
    sort_by: sort ? sort.id : undefined,
    sort_order: sort && (sort.desc ? 'desc' : 'asc'),
    domain: filterInstance.domain,
  });

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    additionalStreet: false,
    email: false,
  });

  const table = useReactTable({
    data: returns?.items || [],
    columns: getColumns({ t }),
    state: {
      rowSelection,
      pagination,
      columnVisibility,
      sorting,
    },
    enableRowSelection: true,
    enableSorting: true,
    columnResizeMode: 'onChange',
    manualSorting: true,
    enableColumnResizing: true,
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      setPagination({ pageIndex: 0, pageSize: Number(selectedOption.value) });
      if (containerRef) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    const newIndex = direction === 'next' ? pagination.pageIndex + 1 : pagination.pageIndex - 1;
    setPagination({ ...pagination, pageIndex: newIndex });
    if (containerRef) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.tableContent}>
      <div ref={rootRef} className={classNames(styles.root)}>
        <div className={styles.columnSelectButton}>
          <div style={{ opacity: loading ? 0 : 1 }}>
            <Button tone={ButtonToneProps.SECONDARY} icon="tune" onClick={() => setShowColumnSelect(true)} />
          </div>
          {showColumnSelect && !loading && (
            <ColumnSelectModal buttonLabels={labels} onClose={() => setShowColumnSelect(false)} table={table} />
          )}
        </div>
        <div className={styles.tableWrapper}>
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      {...{
                        key: header.id,
                        colSpan: header.colSpan,
                        style: {
                          minWidth: header.getSize(),
                        },
                      }}>
                      <div
                        role="none"
                        className={styles.headerColumn}
                        onClick={header.column.getToggleSortingHandler()}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <Icon name="accordion-arrow-up" />,
                          desc: <Icon name="accordion-arrow-down" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `resizer ${table.options.columnResizeDirection} ${
                            header.column.getIsResizing() ? 'isResizing' : ''
                          }`,
                          style: {
                            transform: '',
                          },
                        }}>
                        <div />
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={`${cell.column.id !== 'actions' ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (cell.column.id !== 'actions' && cell.column.id !== 'checkbox') {
                          openOverlay(row.original.id as number);
                        }
                      }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="w-full my-20 flex items-center">
          <div className="flex gap-x-10 flex-1 items-center">
            <div>
              <Select onChange={handlePerPageUpdate} options={options} defaultValue={options[1]} />
            </div>
            <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
          </div>
          <div className="ml-auto">
            <TablePagination
              maxPage={returns?.pages || 0}
              onPaginationClick={handleTablePaginationClick}
              currentPage={pagination.pageIndex}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnsTable;
